import { FrequencyRangeSuggestion } from "algorithms/FrequencyRangeSuggestion";
import { As2DeviceType } from "datatypes/FlukeAs2Implementation";
import { Range as IRange } from "datatypes/SoramaLocalApi";
import { AnalysisPresetType } from "modules/modals/AnalysisPreset/AnalysisPresets";
import { UnitSpectrum } from "units/UnitSpectrum";
import { SelectableRange } from "./FrequencySelectionService";

const FrequencySuggestionConstants = {
    SMOOTHING_WINDOW_WIDTH: {
        leak: 100,
        mechanical: 100,
        partialDischarge: 1000,
        default: 100,
    } as Record<AnalysisPresetType | "default", number>,

    MIN_PEAK_DISTANCE: {
        [As2DeviceType.Ii900]: {
            leak: 1000,
            mechanical: 1000,
            partialDischarge: 1000,
            default: 1000,
        },
        [As2DeviceType.Ii910]: {
            leak: 2000,
            mechanical: 1000,
            partialDischarge: 2000,
            default: 2000,
        },
        default: {
            leak: 2000,
            mechanical: 1000,
            partialDischarge: 2000,
            default: 2000,
        },
    } as Record<As2DeviceType | "default", Record<AnalysisPresetType | "default", number>>,

    WIDTH_THRESHOLD: {
        leak: 100,
        mechanical: 100,
        partialDischarge: 500,
        default: 100,
    } as Record<AnalysisPresetType | "default", number>,

    FREQUENCY_RANGES: {
        [As2DeviceType.Ii900]: {
            default: { from: 10000, to: 50000 },
            leak: { from: 11000, to: 40000 },
            mechanical: { from: 10000, to: 50000 },
            partialDischarge: { from: 10000, to: 40000 },
        },
        [As2DeviceType.Ii910]: {
            default: { from: 10000, to: 58000 },
            leak: { from: 20000, to: 50000 },
            mechanical: { from: 20000, to: 58000 },
            partialDischarge: { from: 10000, to: 50000 },
        },
        default: {
            default: { from: 10000, to: 58000 },
            leak: { from: 10000, to: 58000 },
            mechanical: { from: 10000, to: 58000 },
            partialDischarge: { from: 10000, to: 58000 },
        },
    } as Record<As2DeviceType | "default", Record<AnalysisPresetType | "default", IRange>>,

    ALGORITHM_DEFAULTS: {
        maxNumberOfPeaks: 20,
        maxNumberOfOutputRanges: 3,
        peakWindow: 4800,
    },
} as const;

/**
 * Service to suggest frequency ranges based on device type and analysis preset
 */
export class FrequencySuggestionService {
    public areSuggestionsComputed = false;

    public createSuggestedRanges(
        spectrum: UnitSpectrum,
        device: As2DeviceType | "default" = "default",
        presetType: AnalysisPresetType | "default" = "default"
    ): SelectableRange[] {
        this.areSuggestionsComputed = false;

        const suggestedRanges = FrequencyRangeSuggestion.computeSuggestedFrequencies(spectrum, {
            minFrequency: FrequencySuggestionConstants.FREQUENCY_RANGES[device][presetType].from,
            maxFrequency: FrequencySuggestionConstants.FREQUENCY_RANGES[device][presetType].to,
            smoothingWindow: FrequencySuggestionConstants.SMOOTHING_WINDOW_WIDTH[presetType],
            minPeakDistance: FrequencySuggestionConstants.MIN_PEAK_DISTANCE[device][presetType],
            widthThreshold: FrequencySuggestionConstants.WIDTH_THRESHOLD[presetType],
            maxNumberOfPeaks: FrequencySuggestionConstants.ALGORITHM_DEFAULTS.maxNumberOfPeaks,
            maxNumberOfOutputRanges: FrequencySuggestionConstants.ALGORITHM_DEFAULTS.maxNumberOfOutputRanges,
            peakWindow: FrequencySuggestionConstants.ALGORITHM_DEFAULTS.peakWindow,
        }).map((range) => new SelectableRange(range.from, range.to));

        // Select the first one
        if (suggestedRanges[0]) {
            suggestedRanges[0].isSelected = true;
        }
        this.areSuggestionsComputed = true;
        return suggestedRanges;
    }
}
